import React, { useState, useContext, useMemo, useEffect } from 'react';
import { AppContext } from '../context/AppContext';
import ContractDocumentView from '../components/ContractDocumentView';
import ContractSignModal from '../components/ContractSignModal';
import ContractCreateModal from '../components/ContractCreateModal';
import CompanySealModal from '../components/CompanySealModal';
import ResignationSubmitModal from '../components/ResignationSubmitModal';
import ResignationWithdrawModal from '../components/ResignationWithdrawModal';
import ResignationPolicyModal from '../components/ResignationPolicyModal';
import { downloadContractPdf, printContractDocument } from '../utils/contractPdfHelper';
import { 
  FileText, 
  PenTool, 
  CheckCircle2, 
  Clock, 
  Users, 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Printer, 
  Share2, 
  Copy, 
  Check, 
  Mail, 
  Send, 
  ShieldCheck, 
  Building2, 
  Sparkles, 
  Eye, 
  Trash2, 
  AlertCircle,
  X,
  BookmarkCheck,
  RotateCcw,
  RotateCw,
  Lock,
  Calendar,
  Stamp,
  SlidersHorizontal,
  AlertTriangle
} from 'lucide-react';

const ContractTab = () => {
  const { 
    contracts, 
    currentUser, 
    deleteContract, 
    requestContractDeletion,
    acceptContractDeletion,
    rejectContractDeletion,
    sendContractReminder, 
    extendContractExpiration,
    signContract,
    contractTemplates,
    companySeal,
    updateCompanySeal,
    fetchRemoteContracts
  } = useContext(AppContext);

  const [isSealModalOpen, setIsSealModalOpen] = useState(false);
  const [isPolicyModalOpen, setIsPolicyModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const isAdmin = currentUser?.Role === 'ADMIN' || currentUser?.UserID === 'emp-09' || currentUser?.UserID === 'emp-10' || currentUser?.UserID === 'clou0315';

  // Sub tab view for Admin: 'my' (내 계약서) or 'admin' (전사 계약 관리)
  const [activeSubTab, setActiveSubTab] = useState(isAdmin ? 'admin' : 'my');

  // Auto-sync remote contracts when active subtab changes
  useEffect(() => {
    if (fetchRemoteContracts) {
      fetchRemoteContracts();
    }
  }, [activeSubTab]);

  const handleManualRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    if (fetchRemoteContracts) {
      await fetchRemoteContracts();
    }
    setTimeout(() => setIsRefreshing(false), 500);
  };

  // Modals
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isResignationModalOpen, setIsResignationModalOpen] = useState(false);
  const [signingContract, setSigningContract] = useState(null);
  const [viewingContract, setViewingContract] = useState(null);
  const [withdrawingContract, setWithdrawingContract] = useState(null);

  // Requirement 1: Deletion Agreement Modals
  const [deletionRequestModalContract, setDeletionRequestModalContract] = useState(null);
  const [deletionReasonInput, setDeletionReasonInput] = useState('');
  const [deletionAgreementModalContract, setDeletionAgreementModalContract] = useState(null);

  const handleDeleteClick = (c) => {
    const isSigned = c.Status === 'SIGNED' || c.Status === 'APPROVED' || Boolean(c.EmployeeSignedAt);

    if (!isSigned) {
      if (confirm(`'${c.Title}' 미체결 계약서를 삭제하시겠습니까?`)) {
        deleteContract(c.ContractID);
      }
      return;
    }

    // Signed contract -> Mutual Deletion Agreement Required
    if (c.Status === 'DELETE_REQUESTED') {
      const myId = currentUser?.UserID || 'ADMIN';
      const myEmail = (currentUser?.Email || '').toLowerCase();
      const isMyRequest = (c.DeleteRequestedBy === myId) || (c.DeleteRequestedBy && c.DeleteRequestedBy.toLowerCase() === myEmail);

      if (isMyRequest) {
        alert(`[삭제 합의 수락 대기 중]\n상대방(${c.UserName || c.UserEmail || '계약 상대방'})의 삭제 합의 수락을 기다리는 중입니다.\n양측 삭제 합의가 완료되어야 파기 처리됩니다.`);
      } else {
        setDeletionAgreementModalContract(c);
      }
    } else {
      setDeletionRequestModalContract(c);
      setDeletionReasonInput('');
    }
  };

  // Search & Filter & Pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL'); // 'ALL' | 'PENDING' | 'SIGNED' | 'FREELANCER' | 'RESIGNATION'
  const [periodFilter, setPeriodFilter] = useState('ALL'); // 'ALL' | 'TODAY' | 'MONTH' | 'YEAR' | 'CUSTOM'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [copiedId, setCopiedId] = useState(null);

  // 1. My Contracts (Filtered for current user)
  const myContracts = useMemo(() => {
    if (!currentUser) return [];
    const myEmail = (currentUser.Email || '').toLowerCase();
    const myId = currentUser.UserID;
    const myName = currentUser.Name;

    return contracts.filter(c => {
      if (c.UserID && c.UserID === myId) return true;
      if (c.UserEmail && c.UserEmail.toLowerCase() === myEmail) return true;
      if (c.UserName === myName) return true;
      return false;
    });
  }, [contracts, currentUser]);

  const activeResignation = useMemo(() => {
    return myContracts.find(c => c.IsResignation && c.Status !== 'APPROVED' && c.Status !== 'REJECTED' && c.Status !== 'WITHDRAWN');
  }, [myContracts]);

  const myPendingContracts = useMemo(() => myContracts.filter(c => c.Status === 'PENDING' && !c.IsResignation), [myContracts]);
  const mySignedContracts = useMemo(() => myContracts.filter(c => c.Status === 'SIGNED' || c.Status === 'APPROVED' || c.Status === 'DELETE_REQUESTED' || c.IsResignation), [myContracts]);

  // KPI Stats
  const totalCount = contracts.length;
  const signedCount = contracts.filter(c => c.Status === 'SIGNED' || c.Status === 'APPROVED' || c.Status === 'DELETE_REQUESTED').length;
  const pendingCount = contracts.filter(c => c.Status === 'PENDING').length;
  const freelanceCount = contracts.filter(c => c.TargetType === 'FREELANCER').length;
  const resignationCount = contracts.filter(c => Boolean(c.IsResignation || c.ContractType === 'RESIGNATION' || c.ContractType === 'RESIGNATION_NDA' || c.ContractType === 'HANDOVER')).length;
  const completionRate = totalCount > 0 ? Math.round((signedCount / totalCount) * 100) : 100;

  // Reset page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, periodFilter, customStartDate, customEndDate]);

  // 2. Target Contracts Filtered (Enforces strict self-ownership isolation for non-admins)
  const targetContractList = useMemo(() => {
    if (!isAdmin || activeSubTab === 'my') {
      return myContracts;
    }
    return contracts;
  }, [isAdmin, activeSubTab, myContracts, contracts]);

  const filteredAllContracts = useMemo(() => {
    return targetContractList.filter(c => {
      // Search match
      const matchSearch = !searchTerm || 
        (c.Title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.UserName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.Department || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.ContractID || '').toLowerCase().includes(searchTerm.toLowerCase());

      // Status match
      let matchStatus = true;
      if (statusFilter === 'PENDING') matchStatus = c.Status === 'PENDING';
      else if (statusFilter === 'SIGNED') matchStatus = c.Status === 'SIGNED' || c.Status === 'APPROVED' || c.Status === 'DELETE_REQUESTED';
      else if (statusFilter === 'FREELANCER') matchStatus = c.TargetType === 'FREELANCER';
      else if (statusFilter === 'RESIGNATION') matchStatus = Boolean(c.IsResignation || c.ContractType === 'RESIGNATION' || c.ContractType === 'RESIGNATION_NDA' || c.ContractType === 'HANDOVER');

      // Period match
      let matchPeriod = true;
      const targetDateStr = c.CreatedAt ? c.CreatedAt.slice(0, 10) : (c.ContractStartDate || '');
      if (targetDateStr && periodFilter !== 'ALL') {
        const today = new Date().toISOString().slice(0, 10);
        const thisMonth = today.slice(0, 7);
        const thisYear = today.slice(0, 4);

        if (periodFilter === 'TODAY') {
          matchPeriod = targetDateStr === today;
        } else if (periodFilter === 'MONTH') {
          matchPeriod = targetDateStr.startsWith(thisMonth);
        } else if (periodFilter === 'YEAR') {
          matchPeriod = targetDateStr.startsWith(thisYear);
        } else if (periodFilter === 'CUSTOM') {
          if (customStartDate && targetDateStr < customStartDate) matchPeriod = false;
          if (customEndDate && targetDateStr > customEndDate) matchPeriod = false;
        }
      }

      return matchSearch && matchStatus && matchPeriod;
    });
  }, [targetContractList, searchTerm, statusFilter, periodFilter, customStartDate, customEndDate]);

  // Pagination calculation
  const totalPages = Math.max(1, Math.ceil(filteredAllContracts.length / itemsPerPage));
  const pagedContracts = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredAllContracts.slice(start, start + itemsPerPage);
  }, [filteredAllContracts, currentPage, itemsPerPage]);

  // Copy KakaoTalk Link
  const handleCopyLink = (contract) => {
    const url = `${window.location.origin}/#/contract/sign?token=${contract.PublicToken}`;
    navigator.clipboard.writeText(url);
    setCopiedId(contract.ContractID);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Send Reminder
  const handleSendReminder = async (contractId) => {
    const ok = await sendContractReminder(contractId);
    if (ok) {
      alert('서명 재촉 안내 메일이 대상자에게 즉시 발송되었습니다.');
    } else {
      alert('발송 실패: 대상자 이메일 주소를 확인해 주세요.');
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] bg-slate-50 text-slate-800 text-left">
      {/* Top Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-5 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-2xs">
        <div className="flex items-center gap-3">
          <div className="bg-indigo-50 text-indigo-600 p-2.5 rounded-xl border border-indigo-200">
            <FileText className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              전자계약 및 서약서 관리
              {myPendingContracts.length > 0 && (
                <span className="bg-rose-500 text-white text-[11px] font-black px-2 py-0.5 rounded-full animate-pulse">
                  서명 대기 {myPendingContracts.length}건
                </span>
              )}
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              표준 근로계약서, 연봉계약서, 프리랜서 용역계약서를 법적 효력을 갖춘 전자서명으로 안전하게 체결하고 보관합니다.
            </p>
          </div>
        </div>

        {/* Subtab Toggle if Admin */}
        <div className="flex items-center gap-2">
          {isAdmin && (
            <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200">
              <button
                onClick={() => setActiveSubTab('admin')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  activeSubTab === 'admin'
                    ? 'bg-white text-indigo-700 shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                전사 계약 관리
              </button>
              <button
                onClick={() => setActiveSubTab('my')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                  activeSubTab === 'my'
                    ? 'bg-white text-indigo-700 shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                내 계약서
                {myPendingContracts.length > 0 && (
                  <span className="w-2 h-2 rounded-full bg-rose-500"></span>
                )}
              </button>
            </div>
          )}

          {/* Refresh & Sync Button */}
          <button
            type="button"
            onClick={handleManualRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl border border-slate-300 transition-all cursor-pointer shadow-2xs active:scale-95"
            title="DynamoDB 최신 계약 데이터 실시간 동기화"
          >
            <RotateCw className={`w-3.5 h-3.5 text-indigo-600 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">동기화 새로고침</span>
          </button>

          {isAdmin && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsPolicyModalOpen(true)}
                className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold rounded-xl border border-slate-300 transition-all cursor-pointer shadow-2xs"
                title="사직원 표준 조항 및 인수인계 정책 관리"
              >
                <SlidersHorizontal className="w-3.5 h-3.5 text-indigo-600" />
                <span className="hidden sm:inline">사직원 양식 설정</span>
              </button>
              <button
                onClick={() => setIsSealModalOpen(true)}
                className="flex items-center gap-1.5 px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold rounded-xl border border-slate-300 transition-all cursor-pointer shadow-2xs"
              >
                <Stamp className="w-4 h-4 text-rose-600" />
                회사 직인 설정
              </button>
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-sm transition-all active:scale-98 cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                새 계약서 작성
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="p-6 md:p-8 max-w-7xl mx-auto space-y-6">
        
        {/* ========================================================================= */}
        {/* VIEW 1: MY CONTRACTS (일반 임직원 뷰 & 본인 보관함)                         */}
        {/* ========================================================================= */}
        {activeSubTab === 'my' && (
          <div className="space-y-6">

            {/* Party B Deletion Requested Action Alert Bar */}
            {(() => {
              const pendingDeletionForMe = myContracts.filter(c => {
                if (c.Status !== 'DELETE_REQUESTED') return false;
                const myId = currentUser?.UserID || 'ADMIN';
                const myEmail = (currentUser?.Email || '').toLowerCase();
                const isMyRequest = (c.DeleteRequestedBy === myId) || (c.DeleteRequestedBy && c.DeleteRequestedBy.toLowerCase() === myEmail);
                return !isMyRequest;
              });

              if (pendingDeletionForMe.length === 0) return null;

              return (
                <div className="bg-rose-50 border-2 border-rose-300 rounded-2xl p-4 flex items-center justify-between shadow-sm animate-pulse">
                  <div className="flex items-center gap-3 text-rose-900">
                    <AlertTriangle className="w-6 h-6 text-rose-600 shrink-0" />
                    <div>
                      <strong className="text-xs font-black block text-rose-950">
                        [상대방의 계약서 파기 합의 요청 도착] {pendingDeletionForMe.length}건의 계약서 파기 동의 요청이 도착했습니다.
                      </strong>
                      <p className="text-[11px] text-rose-700 font-medium">
                        상대방(갑)이 체결된 계약서 파기를 요청했습니다. 아래 목록에서 계약서의 삭제(🗑️) 버튼을 클릭하여 파기에 승인 또는 거절해 주세요.
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Resignation / Termination Single Action Banner */}
            {activeResignation ? (
              <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 rounded-2xl p-5 text-white shadow-md flex flex-col md:flex-row md:items-center justify-between gap-4 border border-amber-500/30">
                <div className="flex items-center gap-3.5">
                  <div className="w-11 h-11 rounded-2xl bg-amber-600 flex items-center justify-center text-white shrink-0 shadow-sm animate-pulse">
                    <Clock className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-bold">임직원 퇴사 신청 결재 진행 중 ({activeResignation.CurrentStep || 1}단계)</h3>
                      <span className="text-[10px] bg-amber-500/30 text-amber-200 border border-amber-400/40 px-2 py-0.5 rounded-full font-bold">
                        {activeResignation.CurrentStep === 1 ? '1단계: 인수인계 확인 대기' :
                         activeResignation.CurrentStep === 2 ? '2단계: 소속 팀장 결재 대기' :
                         activeResignation.CurrentStep === 3 ? '3단계: 2차 권한자 결재 대기' :
                         '4단계: 대표이사 최종 결재 대기'}
                      </span>
                    </div>
                    <p className="text-xs text-slate-300 mt-0.5">
                      현재 상신된 사직원의 결재 절차가 진행 중입니다. 퇴사 프로세스가 완료되거나 취하되기 전까지 추가 중복 신청은 비활성화됩니다.
                    </p>
                    <div className="flex flex-wrap items-center gap-2 mt-2 text-[11px] text-amber-200/90 font-medium">
                      <span>• 인수인계자: {activeResignation.HandoverRecipientName || activeResignation.HandoverRecipient || '지정 담당자'}</span>
                      <span>• 희망퇴사일: {activeResignation.ResignationDate || '-'}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    disabled={true}
                    className="px-4 py-2.5 bg-slate-700 text-slate-400 text-xs font-bold rounded-xl shadow-xs cursor-not-allowed opacity-75 flex items-center gap-2 justify-center"
                  >
                    <Lock className="w-4 h-4" />
                    퇴사 신청 진행 중 (잠김)
                  </button>
                  <button
                    onClick={() => setWithdrawingContract(activeResignation)}
                    className="px-4 py-2.5 bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold rounded-xl shadow-md transition-all active:scale-98 flex items-center gap-2 justify-center cursor-pointer"
                  >
                    <RotateCcw className="w-4 h-4" />
                    퇴사 취하 신청
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 rounded-2xl p-5 text-white shadow-md flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3.5">
                  <div className="w-11 h-11 rounded-2xl bg-rose-600 flex items-center justify-center text-white shrink-0 shadow-sm">
                    <FileText className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-bold">임직원 퇴사 신청 및 사직원(퇴직원) 제출</h3>
                      <span className="text-[10px] bg-rose-500/30 text-rose-200 border border-rose-400/40 px-2 py-0.5 rounded-full font-bold">HR 전자양식</span>
                    </div>
                    <p className="text-xs text-slate-300 mt-0.5">
                      퇴사 희망일, 퇴직 사유, 업무 인수인계 및 보안 서약서를 전자서명으로 간편하게 작성하여 제출할 수 있습니다.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsResignationModalOpen(true)}
                  className="px-4 py-2.5 bg-rose-600 hover:bg-rose-500 text-white text-xs font-bold rounded-xl shadow-md transition-all active:scale-98 shrink-0 flex items-center gap-2 justify-center cursor-pointer"
                >
                  <PenTool className="w-4 h-4" />
                  사직원 작성 및 전자서명
                </button>
              </div>
            )}
            
            {/* 1. Pending Signature Alert Section */}
            {myPendingContracts.length > 0 && (
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-5 text-white shadow-md">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-xs">
                      <PenTool className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-sm font-bold">서명이 필요한 전자계약서가 {myPendingContracts.length}건 대기 중입니다!</h2>
                      <p className="text-xs text-white/90 mt-0.5">계약 내용을 확인하시고 직접 서명 또는 인감 날인을 완료해 주세요.</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                  {myPendingContracts.map(c => (
                    <div key={c.ContractID} className="bg-white text-slate-900 rounded-xl p-4 shadow-sm flex items-center justify-between">
                      <div>
                        <span className="text-[10px] font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded uppercase">
                          {c.ContractType}
                        </span>
                        <h3 className="text-xs font-bold text-slate-900 mt-1">{c.Title}</h3>
                        <p className="text-[11px] text-slate-500 mt-0.5">발행일: {c.CreatedAt?.slice(0, 10)}</p>
                      </div>
                      <button
                        onClick={() => setSigningContract(c)}
                        className="flex items-center gap-1.5 px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-xs transition-all active:scale-98 cursor-pointer"
                      >
                        <PenTool className="w-3.5 h-3.5" />
                        지금 서명하기
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 2. Permanent My Signed Contracts Archive */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs space-y-4">
              <div className="flex items-center justify-between border-b border-slate-200 pb-4">
                <div className="flex items-center gap-2">
                  <BookmarkCheck className="w-5 h-5 text-indigo-600" />
                  <div>
                    <h2 className="text-sm font-bold text-slate-900">내 계약서 보관함 (Permanent Archive)</h2>
                    <p className="text-xs text-slate-500">
                      체결 완료된 계약서는 365일 24시간 언제든지 열람하고 PDF로 저장할 수 있습니다.
                    </p>
                  </div>
                </div>
                <span className="text-xs font-bold text-slate-500">
                  총 {mySignedContracts.length}건 보관 중
                </span>
              </div>

              {mySignedContracts.length === 0 ? (
                <div className="text-center py-12 text-slate-400 text-xs">
                  보관된 계약서 및 서류 내역이 없습니다.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {mySignedContracts.map(c => (
                    <div key={c.ContractID} className="border border-slate-200 rounded-xl p-4 bg-slate-50/50 hover:bg-white hover:shadow-md transition-all flex flex-col justify-between space-y-3">
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          {c.Status === 'DELETE_REQUESTED' ? (
                            <span className="text-[10px] font-black text-rose-800 bg-rose-100 border border-rose-300 px-2 py-0.5 rounded flex items-center gap-1 animate-pulse">
                              <AlertCircle className="w-3 h-3 text-rose-600" />
                              삭제 합의 대기 중
                            </span>
                          ) : c.Status === 'WITHDRAWN' ? (
                            <span className="text-[10px] font-bold text-slate-700 bg-slate-100 border border-slate-300 px-2 py-0.5 rounded flex items-center gap-1">
                              <RotateCcw className="w-3 h-3 text-slate-500" />
                              취하 완료
                            </span>
                          ) : c.Status === 'REJECTED' ? (
                            <span className="text-[10px] font-bold text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded flex items-center gap-1">
                              <X className="w-3 h-3 text-rose-500" />
                              결재 반려
                            </span>
                          ) : (c.Status === 'SIGNED' || c.Status === 'APPROVED') ? (
                            <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                              {c.IsResignation ? '최종 승인 완료' : '체결 완료'}
                            </span>
                          ) : (
                            <span className="text-[10px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded flex items-center gap-1">
                              <Clock className="w-3 h-3 text-indigo-600" />
                              {c.IsResignation ? `${c.CurrentStep || 1}단계 결재 중` : '서명 대기'}
                            </span>
                          )}
                          <span className="text-[10px] text-slate-400 font-mono">
                            {c.EmployeeSignedAt?.slice(0, 10) || c.CreatedAt?.slice(0, 10)}
                          </span>
                        </div>
                        <h3 className="text-xs font-bold text-slate-900 leading-snug">{c.Title}</h3>
                        <p className="text-[11px] text-slate-500 mt-1">
                          계약기간: {c.ContractStartDate} ~ {c.ContractEndDate || '정규직'}
                        </p>
                      </div>

                      <div className="pt-3 border-t border-slate-200 flex items-center justify-between gap-2">
                        <button
                          onClick={() => setViewingContract(c)}
                          className="flex items-center gap-1 text-xs font-bold text-indigo-600 hover:text-indigo-800 cursor-pointer"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          전문 열람 & 인쇄
                        </button>

                        {c.Status === 'DELETE_REQUESTED' ? (
                          <button
                            onClick={() => handleDeleteClick(c)}
                            className="px-3 py-1 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-bold shadow-xs animate-pulse flex items-center gap-1 cursor-pointer"
                            title="계약서 파기 합의 동의/거절"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            <span>삭제 합의</span>
                          </button>
                        ) : (
                          <button
                            onClick={() => handleDeleteClick(c)}
                            className="px-2.5 py-1 bg-rose-50 hover:bg-rose-100 text-rose-700 rounded-lg text-xs font-bold flex items-center gap-1 cursor-pointer"
                            title="계약서 파기 요청"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* VIEW 2: ADMIN ALL CONTRACTS DASHBOARD (전사 계약 관리 콘솔)                 */}
        {/* ========================================================================= */}
        {activeSubTab === 'admin' && (
          <div className="space-y-6">
            
            {/* KPI Stat Cards (Interactive Filter Trigger) */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div 
                onClick={() => setStatusFilter('ALL')}
                className={`bg-white border p-4 rounded-2xl shadow-xs cursor-pointer transition-all hover:shadow-md hover:scale-[1.01] ${
                  statusFilter === 'ALL' ? 'border-indigo-600 ring-2 ring-indigo-500/20 bg-indigo-50/20' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-500">전사 총 계약</span>
                  <div className="p-2 rounded-xl bg-indigo-50 text-indigo-600">
                    <FileText className="w-4 h-4" />
                  </div>
                </div>
                <div className="text-2xl font-black text-slate-900 mt-2">{totalCount}건</div>
                <div className="text-[11px] text-slate-400 mt-0.5 flex items-center justify-between">
                  <span>정규직 / 연봉 / 프리랜서</span>
                  {statusFilter === 'ALL' && <span className="text-indigo-600 font-bold text-[10px]">선택됨</span>}
                </div>
              </div>

              <div 
                onClick={() => setStatusFilter('SIGNED')}
                className={`bg-white border p-4 rounded-2xl shadow-xs cursor-pointer transition-all hover:shadow-md hover:scale-[1.01] ${
                  statusFilter === 'SIGNED' ? 'border-emerald-600 ring-2 ring-emerald-500/20 bg-emerald-50/20' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-500">체결 완료율</span>
                  <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600">
                    <CheckCircle2 className="w-4 h-4" />
                  </div>
                </div>
                <div className="text-2xl font-black text-emerald-600 mt-2">{completionRate}%</div>
                <div className="text-[11px] text-emerald-700 mt-0.5 flex items-center justify-between">
                  <span>총 {signedCount}건 서명 완료</span>
                  {statusFilter === 'SIGNED' && <span className="text-emerald-700 font-bold text-[10px]">선택됨</span>}
                </div>
              </div>

              <div 
                onClick={() => setStatusFilter('PENDING')}
                className={`bg-white border p-4 rounded-2xl shadow-xs cursor-pointer transition-all hover:shadow-md hover:scale-[1.01] ${
                  statusFilter === 'PENDING' ? 'border-amber-600 ring-2 ring-amber-500/20 bg-amber-50/20' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-500">서명 대기 인원</span>
                  <div className="p-2 rounded-xl bg-amber-50 text-amber-600">
                    <Clock className="w-4 h-4" />
                  </div>
                </div>
                <div className="text-2xl font-black text-amber-600 mt-2">{pendingCount}명</div>
                <div className="text-[11px] text-amber-700 mt-0.5 flex items-center justify-between">
                  <span>1-Click 재촉 알림 가능</span>
                  {statusFilter === 'PENDING' && <span className="text-amber-700 font-bold text-[10px]">선택됨</span>}
                </div>
              </div>

              <div 
                onClick={() => setStatusFilter('RESIGNATION')}
                className={`bg-white border p-4 rounded-2xl shadow-xs cursor-pointer transition-all hover:shadow-md hover:scale-[1.01] ${
                  statusFilter === 'RESIGNATION' ? 'border-rose-600 ring-2 ring-rose-500/20 bg-rose-50/20' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-500">사직원 / 퇴사 결재</span>
                  <div className="p-2 rounded-xl bg-rose-50 text-rose-600">
                    <ShieldCheck className="w-4 h-4" />
                  </div>
                </div>
                <div className="text-2xl font-black text-rose-700 mt-2">{resignationCount}건</div>
                <div className="text-[11px] text-rose-700 mt-0.5 flex items-center justify-between">
                  <span>4단계 결재 승인선 관리</span>
                  {statusFilter === 'RESIGNATION' && <span className="text-rose-700 font-bold text-[10px]">선택됨</span>}
                </div>
              </div>
            </div>

            {/* Filter & Table Box */}
            <div className="bg-white border border-slate-200 rounded-2xl shadow-xs overflow-hidden">
              <div className="p-4 border-b border-slate-200 space-y-3">
                
                {/* Search & Status Chips */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  {/* Search */}
                  <div className="relative flex-1 max-w-sm">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      placeholder="계약서명, 성명, 부서, 문서번호 검색..."
                      className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  {/* Status Chips */}
                  <div className="flex items-center gap-1.5 overflow-x-auto">
                    {[
                      { id: 'ALL', label: `전체 (${totalCount})` },
                      { id: 'PENDING', label: `서명 대기 (${pendingCount})` },
                      { id: 'SIGNED', label: `체결 완료 (${signedCount})` },
                      { id: 'FREELANCER', label: `프리랜서 (${freelanceCount})` },
                      { id: 'RESIGNATION', label: `사직원 (${resignationCount})` }
                    ].map(st => (
                      <button
                        key={st.id}
                        onClick={() => setStatusFilter(st.id)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                          statusFilter === st.id
                            ? 'bg-slate-900 text-white shadow-xs'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {st.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Period Filter (일자별 / 월별 / 년도별 / 직접 지정) */}
                <div className="pt-2 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2 text-xs">
                  <div className="flex items-center gap-1.5 overflow-x-auto">
                    <span className="text-[11px] font-bold text-slate-500 flex items-center gap-1 mr-1">
                      <Calendar className="w-3.5 h-3.5 text-indigo-600" />
                      기간 조회:
                    </span>
                    {[
                      { id: 'ALL', label: '전체 기간' },
                      { id: 'TODAY', label: '오늘' },
                      { id: 'MONTH', label: '이번 달' },
                      { id: 'YEAR', label: '올해' },
                      { id: 'CUSTOM', label: '직접 지정' }
                    ].map(p => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setPeriodFilter(p.id)}
                        className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all cursor-pointer ${
                          periodFilter === p.id
                            ? 'bg-indigo-600 text-white shadow-xs'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>

                  {periodFilter === 'CUSTOM' && (
                    <div className="flex items-center gap-1.5 bg-slate-50 p-1 rounded-lg border border-slate-200 animate-in fade-in duration-100">
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={e => setCustomStartDate(e.target.value)}
                        className="px-2 py-0.5 bg-white border border-slate-300 rounded text-[11px] font-mono"
                      />
                      <span className="text-slate-400">~</span>
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={e => setCustomEndDate(e.target.value)}
                        className="px-2 py-0.5 bg-white border border-slate-300 rounded text-[11px] font-mono"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold">
                      <th className="p-3.5">계약서 제목</th>
                      <th className="p-3.5">계약 대상자</th>
                      <th className="p-3.5">유형</th>
                      <th className="p-3.5">계약 기간</th>
                      <th className="p-3.5">금액 / 연봉</th>
                      <th className="p-3.5">상태</th>
                      <th className="p-3.5 text-right">작업 및 관리</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {pagedContracts.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-12 text-slate-400 text-xs">
                          조회된 전자계약 문서가 없습니다.
                        </td>
                      </tr>
                    ) : (
                      pagedContracts.map(c => {
                        const isFree = c.TargetType === 'FREELANCER';
                        const isResig = Boolean(c.IsResignation || c.ContractType === 'RESIGNATION' || c.ContractType === 'RESIGNATION_NDA' || c.ContractType === 'HANDOVER');
                        const isSigned = c.Status === 'SIGNED' || c.Status === 'APPROVED';

                        return (
                          <tr key={c.ContractID} className="hover:bg-slate-50/80 transition-colors">
                            <td className="p-3.5 font-bold text-slate-900">
                              <div>{c.Title}</div>
                              <div className="text-[10px] text-slate-400 font-mono mt-0.5">DOC-{c.ContractID}</div>
                              {isResig && c.ApprovalLine && c.ApprovalLine.length > 0 && (
                                <div className="flex items-center gap-1 mt-1.5">
                                  {c.ApprovalLine.map(step => {
                                    const isDone = step.Status === 'APPROVED';
                                    const isCurrent = step.Step === c.CurrentStep && c.Status !== 'APPROVED' && c.Status !== 'WITHDRAWN' && c.Status !== 'REJECTED';
                                    return (
                                      <span
                                        key={step.Step}
                                        className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${
                                          isDone ? 'bg-emerald-100 text-emerald-800' :
                                          isCurrent ? 'bg-indigo-600 text-white animate-pulse' :
                                          'bg-slate-100 text-slate-400'
                                        }`}
                                        title={`${step.RoleName}: ${step.ApproverName || ''}`}
                                      >
                                        {step.Step}.{step.Role === 'HANDOVER_ASSIGNEE' ? '인수인계' : step.Role === 'TEAM_LEADER' ? '팀장' : step.Role === 'HR_MANAGER' ? '2차권한' : '대표이사'}
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                            </td>
                            <td className="p-3.5">
                              <div className="font-bold text-indigo-700">{c.UserName}</div>
                              <div className="text-[10px] text-slate-500">
                                {c.Department} {c.Position ? `(${c.Position})` : ''}
                              </div>
                            </td>
                            <td className="p-3.5">
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                                isResig
                                  ? 'bg-rose-50 text-rose-700 border border-rose-200'
                                  : isFree 
                                  ? 'bg-cyan-50 text-cyan-700 border border-cyan-200'
                                  : 'bg-slate-100 text-slate-700'
                              }`}>
                                {isResig ? '사직원(퇴사)' : isFree ? '외부 프리랜서' : '사내 임직원'}
                              </span>
                            </td>
                            <td className="p-3.5 text-slate-600 font-medium">
                              {c.ContractStartDate} ~ {c.ContractEndDate || '정규직'}
                            </td>
                            <td className="p-3.5 font-mono font-bold text-slate-900">
                              {isResig 
                                ? '-' 
                                : isAdmin 
                                ? `₩ ${(c.TotalAmount || c.AnnualSalary || 0).toLocaleString()} 원` 
                                : <span className="text-slate-400 text-xs font-sans font-medium flex items-center gap-1"><Lock className="w-3 h-3 text-slate-400" /> 보안 정보 (전문열람 참조)</span>
                              }
                            </td>
                            <td className="p-3.5">
                              {(() => {
                                const isExpired = c.TokenExpiresAt && !isSigned && c.Status === 'PENDING' && new Date(c.TokenExpiresAt) < new Date();
                                if (c.Status === 'DELETE_REQUESTED') {
                                  return (
                                    <span className="inline-flex items-center gap-1 bg-rose-100 text-rose-800 border border-rose-300 text-[10px] font-black px-2 py-0.5 rounded-full animate-pulse">
                                      <AlertCircle className="w-3 h-3" />
                                      삭제 합의 대기 중
                                    </span>
                                  );
                                }
                                if (isExpired) {
                                  return (
                                    <span className="inline-flex items-center gap-1 bg-rose-50 text-rose-700 border border-rose-200 text-[10px] font-black px-2 py-0.5 rounded-full">
                                      <Clock className="w-3 h-3 text-rose-600" />
                                      서명 기한 만료
                                    </span>
                                  );
                                }
                                if (c.Status === 'WITHDRAWN') {
                                  return (
                                    <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 border border-slate-300 text-[10px] font-bold px-2 py-0.5 rounded-full">
                                      <RotateCcw className="w-3 h-3 text-slate-500" />
                                      퇴사 취하
                                    </span>
                                  );
                                }
                                if (c.Status === 'REJECTED') {
                                  return (
                                    <span className="inline-flex items-center gap-1 bg-rose-50 text-rose-700 border border-rose-200 text-[10px] font-bold px-2 py-0.5 rounded-full">
                                      <X className="w-3 h-3" />
                                      결재 반려
                                    </span>
                                  );
                                }
                                if (isSigned) {
                                  return (
                                    <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold px-2 py-0.5 rounded-full">
                                      <CheckCircle2 className="w-3 h-3" />
                                      {isResig ? '최종 승인' : '체결 완료'}
                                    </span>
                                  );
                                }
                                return (
                                  <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-bold px-2 py-0.5 rounded-full">
                                    <Clock className="w-3 h-3" />
                                    {isResig ? `${c.CurrentStep || 1}단계 결재 대기` : '서명 대기'}
                                  </span>
                                );
                              })()}
                            </td>
                            <td className="p-3.5 text-right space-x-1.5 whitespace-nowrap">
                              {/* View Document */}
                              <button
                                onClick={() => setViewingContract(c)}
                                title="계약서 전문 열람"
                                className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
                              >
                                <Eye className="w-3.5 h-3.5" />
                              </button>

                              {/* KakaoTalk / Link Copy */}
                              <button
                                onClick={() => handleCopyLink(c)}
                                title="카카오톡 전용 링크 복사"
                                className="p-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 transition-colors"
                              >
                                {copiedId === c.ContractID ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Share2 className="w-3.5 h-3.5" />}
                              </button>

                              {/* Extend Expiration Date (+7 days) */}
                              {!isSigned && (
                                <button
                                  onClick={async () => {
                                    const newDate = await extendContractExpiration(c.ContractID, 7);
                                    alert(`'${c.Title}' 서명 링크 유효기간이 7일 연장되었습니다.\n(신규 만료일: ${new Date(newDate).toLocaleDateString()})`);
                                  }}
                                  title="서명 링크 유효기간 7일 재연장"
                                  className="p-1.5 rounded-lg bg-sky-50 hover:bg-sky-100 text-sky-700 transition-colors"
                                >
                                  <RotateCw className="w-3.5 h-3.5" />
                                </button>
                              )}

                              {/* Reminder Email if pending */}
                              {!isSigned && c.UserEmail && (
                                <button
                                  onClick={() => handleSendReminder(c.ContractID)}
                                  title="서명 독려 메일 발송"
                                  className="p-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-700 transition-colors"
                                >
                                  <Send className="w-3.5 h-3.5" />
                                </button>
                              )}

                              {/* Delete */}
                              <button
                                onClick={() => handleDeleteClick(c)}
                                title={isSigned ? "계약서 삭제/파기 합의 요청" : "삭제"}
                                className={`p-1.5 rounded-lg transition-colors ${
                                  c.Status === 'DELETE_REQUESTED'
                                    ? 'bg-rose-600 text-white animate-pulse'
                                    : 'bg-rose-50 hover:bg-rose-100 text-rose-600'
                                }`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* 10-Item Pagination Bar */}
              <div className="p-4 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-600">
                <div>
                  총 <strong className="text-slate-900 font-bold">{filteredAllContracts.length}</strong>건 중{' '}
                  {filteredAllContracts.length > 0 ? (
                    <span>
                      {((currentPage - 1) * itemsPerPage) + 1} ~ {Math.min(currentPage * itemsPerPage, filteredAllContracts.length)}건 표시
                    </span>
                  ) : '0건'}
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      className="px-2.5 py-1 rounded-lg border border-slate-200 bg-white font-bold text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                    >
                      이전
                    </button>

                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        type="button"
                        onClick={() => setCurrentPage(page)}
                        className={`w-7 h-7 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                          currentPage === page
                            ? 'bg-indigo-600 text-white shadow-xs'
                            : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        {page}
                      </button>
                    ))}

                    <button
                      type="button"
                      disabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      className="px-2.5 py-1 rounded-lg border border-slate-200 bg-white font-bold text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                    >
                      다음
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Contract Sign Modal */}
      {signingContract && (
        <ContractSignModal
          contract={signingContract}
          isOpen={true}
          onClose={() => setSigningContract(null)}
          onSignSuccess={async (cId, sigData, audit) => {
            await signContract(cId, sigData, audit);
            setSigningContract(null);
          }}
        />
      )}

      {/* Company Seal Modal */}
      {isSealModalOpen && (
        <CompanySealModal
          isOpen={true}
          onClose={() => setIsSealModalOpen(false)}
        />
      )}

      {/* Resignation Policy & Clauses Modal */}
      {isPolicyModalOpen && (
        <ResignationPolicyModal
          isOpen={true}
          onClose={() => setIsPolicyModalOpen(false)}
        />
      )}

      {/* Contract Create Modal */}
      {isCreateModalOpen && (
        <ContractCreateModal
          isOpen={true}
          onClose={() => setIsCreateModalOpen(false)}
          onCreatedSuccess={() => {}}
        />
      )}

      {/* Resignation Submit Modal */}
      {isResignationModalOpen && (
        <ResignationSubmitModal
          isOpen={true}
          onClose={() => setIsResignationModalOpen(false)}
          onSubmittedSuccess={() => {}}
        />
      )}

      {/* Contract View Only Modal */}
      {viewingContract && (
        <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-xs flex items-center justify-center p-2 md:p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[92vh] flex flex-col shadow-2xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 bg-slate-900 text-white flex items-center justify-between">
              <h2 className="text-sm font-bold">{viewingContract.Title} - 전문 열람</h2>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const target = document.getElementById(`contract-doc-${viewingContract.ContractID}`) || document.getElementById('contract-view-target');
                    downloadContractPdf(target, viewingContract.Title);
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition-all shadow-2xs cursor-pointer"
                  title="PDF 파일로 자동 저장 및 다운로드"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>PDF 다운로드</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const target = document.getElementById(`contract-doc-${viewingContract.ContractID}`) || document.getElementById('contract-view-target');
                    printContractDocument(target, viewingContract.Title);
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold transition-all shadow-2xs cursor-pointer"
                  title="A4 다중 페이지 인쇄 / PDF 저장"
                >
                  <Printer className="w-3.5 h-3.5" />
                  <span>PDF / 인쇄 출력</span>
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setViewingContract(null);
                  }}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors cursor-pointer relative z-50 ml-1"
                  title="닫기"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-slate-100" id="contract-view-target">
              <ContractDocumentView 
                contract={contracts.find(c => c.ContractID === viewingContract?.ContractID || (c.PublicToken && c.PublicToken === viewingContract?.PublicToken)) || viewingContract} 
                onOpenWithdrawModal={(c) => setWithdrawingContract(c)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Requirement 1: Request Deletion Modal */}
      {deletionRequestModalContract && (
        <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-slate-200 text-left space-y-4">
            <div className="flex items-center gap-3 text-rose-600 border-b border-rose-100 pb-3">
              <AlertTriangle className="w-6 h-6 flex-shrink-0" />
              <div>
                <h3 className="font-extrabold text-base text-slate-900">체결 계약서 삭제/파기 합의 요청</h3>
                <p className="text-xs text-rose-600 font-medium">체결된 계약서는 상대방의 합의가 있어야 삭제됩니다.</p>
              </div>
            </div>

            <div className="text-xs text-slate-600 space-y-2 bg-slate-50 p-3.5 rounded-xl border border-slate-200">
              <p><strong>계약서명:</strong> {deletionRequestModalContract.Title}</p>
              <p><strong>계약 당사자:</strong> {deletionRequestModalContract.UserName || deletionRequestModalContract.UserEmail || '상대방'}</p>
              <p className="text-[11px] text-slate-500">본 합의 요청을 발송하면 상대방에게 파기 동의 요청 메일/알림이 전달됩니다. 상대방이 동의해야 최종 파기됩니다.</p>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700 block">삭제/파기 사유 (선택 입력):</label>
              <textarea
                rows={3}
                placeholder="예: 프로젝트 변경으로 인한 계약 상호 해지 및 파기 합의..."
                value={deletionReasonInput}
                onChange={(e) => setDeletionReasonInput(e.target.value)}
                className="w-full p-2.5 bg-white border border-slate-300 rounded-xl text-xs focus:outline-none focus:border-rose-500"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setDeletionRequestModalContract(null)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl"
              >
                취소
              </button>
              <button
                type="button"
                onClick={async () => {
                  await requestContractDeletion(deletionRequestModalContract.ContractID, deletionReasonInput);
                  alert(`'${deletionRequestModalContract.Title}' 계약서의 삭제 합의 요청이 상대방에게 전달되었습니다.`);
                  setDeletionRequestModalContract(null);
                }}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-xl shadow-2xs"
              >
                상대방에게 삭제 합의 요청 발송
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Requirement 1: Deletion Agreement Response Modal (Counterparty viewing request) */}
      {deletionAgreementModalContract && (
        <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-slate-200 text-left space-y-4">
            <div className="flex items-center gap-3 text-amber-600 border-b border-amber-100 pb-3">
              <AlertCircle className="w-6 h-6 flex-shrink-0" />
              <div>
                <h3 className="font-extrabold text-base text-slate-900">상대방의 계약서 삭제/파기 합의 요청</h3>
                <p className="text-xs text-amber-600 font-medium">상대 당사자가 계약 파기 동의를 요청했습니다.</p>
              </div>
            </div>

            <div className="text-xs text-slate-700 space-y-2 bg-amber-50/50 p-4 rounded-xl border border-amber-200">
              <p><strong>계약 문서:</strong> {deletionAgreementModalContract.Title}</p>
              <p><strong>요청 신청자:</strong> {deletionAgreementModalContract.DeleteRequestedByName || '상대 당사자'}</p>
              {deletionAgreementModalContract.DeleteRequestedReason && (
                <p><strong>신청 사유:</strong> {deletionAgreementModalContract.DeleteRequestedReason}</p>
              )}
              <p className="text-[11px] text-slate-500 pt-1">수락 시 본 체결 계약서는 즉시 영구 파기(삭제)되며, 거절 시 계약은 그대로 유효하게 유지됩니다.</p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={async () => {
                  if (confirm('삭제 합의 요청을 거절하시겠습니까? (체결된 계약서가 계속 유지됩니다)')) {
                    await rejectContractDeletion(deletionAgreementModalContract.ContractID);
                    alert('삭제 합의 요청을 거절했습니다. 체결 계약서가 유지됩니다.');
                    setDeletionAgreementModalContract(null);
                  }
                }}
                className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-800 text-xs font-bold rounded-xl"
              >
                삭제 거절 (계약 유지)
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (confirm('계약서 삭제/파기에 합의하여 영구 삭제하시겠습니까?')) {
                    await acceptContractDeletion(deletionAgreementModalContract.ContractID);
                    alert('양측 상호 합의에 의해 계약서가 영구 파기되었습니다.');
                    setDeletionAgreementModalContract(null);
                  }
                }}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-xl shadow-2xs"
              >
                삭제 합의 수락 및 영구 파기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resignation Withdrawal & Counseling Modal */}
      {withdrawingContract && (
        <ResignationWithdrawModal
          contract={withdrawingContract}
          isOpen={Boolean(withdrawingContract)}
          onClose={() => setWithdrawingContract(null)}
          onWithdrawnSuccess={() => {
            setWithdrawingContract(null);
            if (viewingContract) setViewingContract(null);
          }}
        />
      )}

      {/* Company Seal Settings Modal */}
      <CompanySealModal
        isOpen={isSealModalOpen}
        onClose={() => setIsSealModalOpen(false)}
        currentSeal={companySeal}
        onSave={updateCompanySeal}
      />
    </div>
  );
};

export default ContractTab;
